<template>
  <app-panel class="diagnostics-filter">
    <label slot="panel-header">
      {{ $t('fields.general.filters') }}
    </label>
    <div slot="panel-body">
      <b-container fluid>
        <b-row class="mb-3 mt-2">
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.module') }}
            </label>
            <s-select
              v-model="filter.module"
              value-field="id"
              text-field="name"
              :options="modulesOptions"
            />
          </b-col>
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.norm') }}
            </label>
            <s-select
              v-model="filter.norm"
              value-field="id"
              text-field="name"
              :options="norms"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.classification') }}
            </label>
            <s-enum-select
              v-model="filter.classification"
              :enum-definition="classifications"
              selectOptionTranslation="flags-dropdown.all-female"
              translation-path="enums.engineering.diagnostics-history"
            />
          </b-col>
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.generates-alarm') }}
            </label>
            <s-select
              v-model="filter.generatesAlarm"
              :options="generatesAlarmOptions"
            />
          </b-col>
        </b-row>
        <b-checkbox
          class="mb-3"
          v-model="showAdvancedFilters"
        >
          <span class="font-weight-normal">
            {{ $t('diagnostics-configurations.advanced-filters') }}
          </span>
        </b-checkbox>
        <div
          class="diagnostics-filter__advanced-filters"
          :class="{ 'diagnostics-filter__advanced-filters_collapsed': !showAdvancedFilters }"
        >
          <diagnostics-list
            class="mb-3"
            :chromatography="chromatographyDiagnostics"
            :physical-chemical="physicalChemicalDiagnostics"
            :selected-chromatography-diagnostics.sync="selectedChromatographyDiagnostics"
            :selected-physical-chemical-diagnostics.sync="selectedPhysicalChemicalDiagnostics"
          />
          <reports-filter
            class="mb-5"
            dropdown-direction="below"
            :filter.sync="filter.targetAssets"
          />
        </div>
        <b-row
          class="diagnostics-configurations__form-row"
          align-h="end"
        >
          <b-col
            class="p-md-0 mb-3"
            sm="12"
            md="auto"
          >
            <b-button
              class="diagnostics-configurations__filters-buttons"
              block
              @click="cleanAllFilters"
            > {{ $t('general.clean-filters') }}
            </b-button>
          </b-col>
          <b-col
            class="pl-md-2 mb-3"
            sm="12"
            md="auto"
          >
            <b-button
              class="diagnostics-configurations__filters-buttons"
              block
              variant="primary"
              @click="search"
            > {{ $t('general.search') }}
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </app-panel>
</template>

<script>
import {
  normsMap,
  rulesMap,
  ClassificationType,
  Modules,
} from 'sigma-mappings';
import diagnosticsService from '@/domain/services/generalPanel/reports/diagnostics';
import { events } from '@/domain/services/generalPanel/reports/localEventBus';
import DiagnosticsList from '@/domain/components/generalPanel/reports/diagnosticsHistory/DiagnosticList';
import ReportsFilter from '@/components/ReportsFilter';
import globalEventBus from '@/services/globalEventBus';

export default {
  name: 'DiagnosticsFilter',
  components: {
    DiagnosticsList,
    ReportsFilter,
  },
  data: vm => ({
    selectedChromatographyDiagnostics: [],
    selectedPhysicalChemicalDiagnostics: [],
    modules: [],
    diagnosticsFilter: [],
    classifications: ClassificationType,
    showAdvancedFilters: false,
    generatesAlarmOptions: [
      {
        text: vm.$t('flags-dropdown.all-female'),
        value: null,
      },
      {
        text: vm.$t('general.yes'),
        value: true,
      },
      {
        text: vm.$t('general.no'),
        value: false,
      },
    ],
    filter: {
      module: null,
      norm: null,
      classification: null,
      generatesAlarm: null,
      diagnosticsIds: [],
      targetAssets: {},
    },
  }),
  computed: {
    chromatographyDiagnostics() {
      return this.diagnosticsFilter.find(
        diagnosis => diagnosis.module === Modules.CHROMATOGRAPHY,
      );
    },
    physicalChemicalDiagnostics() {
      return this.diagnosticsFilter.find(
        diagnosis => diagnosis.module === Modules.PHYSICAL_CHEMICAL,
      );
    },
    normsEnums() {
      return {
        'physical-chemical': Object.values(normsMap),
        chromatography: Object.values(rulesMap),
      };
    },
    norms() {
      const norms = this.getNormByModules();
      return [
        {
          id: null,
          name: this.$t('flags-dropdown.all-female'),
        },
        ...norms,
      ];
    },
    modulesOptions() {
      return [
        {
          id: null,
          name: this.$t('flags-dropdown.all'),
        },
        {
           id: Modules.CHROMATOGRAPHY,
          name: this.$t('diagnostics-configurations.modules.chromatography'),
        },
        {
          id: Modules.PHYSICAL_CHEMICAL,
          name: this.$t('diagnostics-configurations.modules.physical-chemical'),
        },
      ];
    },
  },
  created() {
    this.getDiagnostics();
  },
  methods: {
    clearDiagnosticsList() {
      this.selectedChromatographyDiagnostics = [];
      this.selectedPhysicalChemicalDiagnostics = [];
    },
    cleanAllFilters() {
      this.filter = {
        module: null,
        norm: null,
        classification: null,
        generatesAlarm: null,
        diagnosticsIds: [],
        targetAssets: {},
      };
      this.clearDiagnosticsList();
      this.clearReportsFilter();
      this.getDiagnostics();
    },
    clearReportsFilter() {
      globalEventBus.emit(events.CLEAR_REPORTS_FILTER);
    },
    nullSetter(value) {
      if (value === null) {
        return value;
      }
      return [value];
    },
    getDiagnostics() {
      let modules = [Modules.PHYSICAL_CHEMICAL, Modules.CHROMATOGRAPHY];
      if (this.filter.module) {
        modules = [this.filter.module];
      }
      const filter = {
        modules,
        norms: this.nullSetter(this.filter.norm),
        classifications: this.nullSetter(this.filter.classification),
        generatesAlarm: this.filter.generatesAlarm,
      };
      diagnosticsService.getDiagnostics(filter).then((response) => {
        this.diagnosticsFilter = response;
      });
    },
    getNormByModules() {
      if (!this.filter.module) {
        return this.getAllNorms();
      }
      const norms = this.normsEnums[this.filter.module] || [];
      return norms.map(item => ({
        id: item,
        name: this.$t(`diagnostics-configurations.modules.norms.${item}`),
      }));
    },
    getAllNorms() {
      const norms = [];
      this.modules.forEach((module) => {
        const moduleNorms = this.normsEnums[module] || [];
        moduleNorms.forEach((normEnum) => {
          norms.push({
            id: normEnum,
            name: this.$t(`diagnostics-configurations.modules.norms.${normEnum}`),
          });
        });
      });
      return norms;
    },
    search() {
      this.filter.diagnosticsIds = [
        ...this.selectedChromatographyDiagnostics,
        ...this.selectedPhysicalChemicalDiagnostics,
      ];
      if (!this.showAdvancedFilters) {
        this.filter.targetAssets = {};
        this.filter.diagnosticsIds = [];
      }
      this.$emit('search', this.filter);
    },
  },
};
</script>

<style lang="sass" scoped>
  .diagnostics-filter
    &__advanced-filters
      max-height: 900px
      overflow-y: visible
      overflow-x: hidden
      transition: max-height 0.2s ease-in-out
      animation: 0.3s hide-scroll backwards
      @keyframes hide-scroll
        from, to
          overflow: hidden
    &__advanced-filters_collapsed
      max-height: 0
      overflow: hidden
      transition: max-height 0.2s ease-in-out
    &__buttons
      display: flex
      justify-content: flex-end
      margin: 10px
</style>

<style lang="sass">
  .diagnostics-filter
    .app-panel__body
      overflow: visible !important
</style>
